import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { useAuthStore } from '@/stores/authStore'
import { getIndex, listIndexes } from '@/lib/indexes/registry'
import { useReadinessStore, useScope, scopeKey } from '@/stores/readinessStore'
import { fetchDiscoveryFacts, fetchPlaybookFacts, mergePlaybookFacts, playbookFactsFromActions } from '@/lib/readiness/facts'
import { useIsLocked } from '@/stores/profileStore'
import { resolveIndexId } from '@/lib/readiness/profile'
import { invalidateSyncedReads, useBackendSync } from '@/lib/readiness/backendSync'

// Shared page hook: resolves org + index (from ?index=, falling back to the
// per-user selection), exposes the scope and the sync action, and emulates the
// daily scheduled job (sync at the configured time) on first render when it is
// overdue (§7.1). The sync state (last run, connections) is qtz-readiness's
// (useBackendSync). The quarterly schedule is a reminder to save an
// assessment, never an automatic save (I2 §1.2): `reminderDue` drives a banner.

const DAY_MS = 86_400_000

// Org and index pairs whose on-open catch-up has run in this page load. Kept
// at module level: every component that calls useReadiness() mounts its own
// copy of the hook, and each would otherwise start the same sync.
const caughtUp = new Set<string>()

export function useActor(): string {
  const email = useAuthStore((s) => s.userEmail)
  const userId = useAuthStore((s) => s.userId)
  return email ?? userId ?? 'you'
}

export function useReadiness() {
  const { currentOrgId: orgId, orgName } = useOrgStore()
  const [params, setParams] = useSearchParams()
  const selected = useReadinessStore((s) => s.selectedIndexId)
  const setIndex = useReadinessStore((s) => s.setIndex)
  const paramIndex = params.get('index')
  // POC §3.2: with the index switch locked only CSA QRI resolves.
  const indexLocked = useIsLocked('index_switch')
  const indexId = useMemo(() => resolveIndexId(paramIndex, selected, indexLocked), [paramIndex, selected, indexLocked])
  const index = useMemo(() => getIndex(indexId), [indexId])
  const scope = useScope(orgId, indexId)
  const actor = useActor()
  const qc = useQueryClient()
  const backend = useBackendSync(orgId, indexId)

  const ensureScope = useReadinessStore((s) => s.ensureScope)
  const runSync = useReadinessStore((s) => s.runSync)
  const failSync = useReadinessStore((s) => s.failSync)
  const snoozeReminder = useReadinessStore((s) => s.snoozeReminder)
  const track = useReadinessStore((s) => s.track)
  useEffect(() => {
    ensureScope(orgId, indexId)
  }, [orgId, indexId, ensureScope])

  // Keep the URL and the persisted selection in step (§5.1, §12).
  const selectIndex = useCallback(
    (id: string) => {
      if (id === indexId || indexLocked) return
      setIndex(id)
      track(orgId, id, { name: 'readiness.index_switched', index_id: id })
      const next = new URLSearchParams(params)
      next.set('index', id)
      setParams(next, { replace: true })
    },
    [params, setParams, setIndex, track, orgId, indexId, indexLocked]
  )

  const [syncing, setSyncing] = useState(false)
  const sync = useCallback(
    async (trigger: 'manual' | 'scheduled' | 'on_open' = 'manual') => {
      if (syncing) return
      setSyncing(true)
      try {
        const [discovery, service] = await Promise.all([fetchDiscoveryFacts(orgId, trigger), fetchPlaybookFacts(orgId, trigger)])
        const playbook = mergePlaybookFacts(
          service,
          playbookFactsFromActions(useReadinessStore.getState().scopes[scopeKey(orgId, indexId)]?.playbook_actions ?? [])
        )
        runSync(orgId, indexId, { discovery, playbook }, trigger, actor)
      } catch (e) {
        failSync(orgId, indexId, e instanceof Error ? e.message : String(e), trigger)
      } finally {
        // The pulls wrote to the backend whatever happened here: re-read it.
        void invalidateSyncedReads(qc, orgId)
        setSyncing(false)
      }
    },
    [orgId, indexId, actor, runSync, failSync, qc, syncing]
  )

  // The runs the page reports: the backend's.
  const runs = backend.data?.runs ?? []
  const lastRun = runs[0]

  // Emulated scheduler: once per page load, run the daily sync if the last run
  // is older than a day, once the backend's runs have loaded. The assessment
  // reminder is surfaced, never acted on.
  useEffect(() => {
    const key = `${orgId}::${indexId}`
    if (caughtUp.has(key) || !orgId || !backend.loaded) return
    caughtUp.add(key)
    const lastAt = lastRun ? new Date(lastRun.started_at).getTime() : 0
    if (lastRun && Date.now() - lastAt > DAY_MS) void sync('on_open')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, indexId, backend.loaded])
  const reminderDue = !!scope.next_scheduled_snapshot && new Date(scope.next_scheduled_snapshot).getTime() <= Date.now()
  const dismissReminder = useCallback(() => snoozeReminder(orgId, indexId), [snoozeReminder, orgId, indexId])

  return {
    orgId,
    orgName,
    indexId,
    index,
    indexes: listIndexes(),
    selectIndex,
    scope,
    actor,
    sync,
    syncing,
    lastSuccess: runs.find((r) => r.status === 'succeeded'),
    lastRun,
    // Whether a pull from each module has succeeded, as the backend's summaries
    // say; null until they answer.
    discoveryConnected: backend.data?.discoveryConnected ?? null,
    playbookConnected: backend.data?.playbookConnected ?? null,
    hasSynced: runs.some((r) => r.status === 'succeeded'),
    // Quarterly reminder to save an assessment (I2 §1.2).
    reminderDue,
    dismissReminder,
  }
}

export type ReadinessCtx = ReturnType<typeof useReadiness>
